'use client';
import React from 'react';
import styled from '@benzinga/themetron';

export interface MediaAlphaInsuranceQuotesProps {
  adId: string;
  hostUrl: string;
  title: string;
  subtitle: string;
  button_text?: string;
  placementId: string;
  pagePlacement: 'top' | 'bottom';
  type: string;
  uaClass: string;
  version: string;
}

declare global {
  interface Window {
    MediaAlphaExchange: {
      data: {
        zip: string;
      };
      placement_id: string;
      sub_1: string;
      sub_2: string;
      type: string;
      ua_class: string;
      version: string;
      error: () => void;
      success: () => void;
    };
    MediaAlphaExchange__load: (adId: string) => void;
  }
}

const getAdId = () => Math.random().toString(36).substring(2, 7) + Math.random().toString(36).substring(2, 7);

export const MediaAlphaInsuranceQuotes: React.FC<MediaAlphaInsuranceQuotesProps> = ({
  button_text,
  pagePlacement = 'top',
  placementId,
  subtitle,
  title,
  type,
  uaClass,
  version = '17',
}) => {
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  const [adId] = React.useMemo(() => getAdId(), [placementId]);
  const [zipcode, setZipcode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [primaryform, setPrimaryForm] = React.useState(true);

  React.useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://insurance.mediaalpha.com/js/serve.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const submitForm = React.useCallback(() => {
    if (!zipcode) {
      alert('Enter zipcode');
      return;
    }
    if (!adId) {
      alert('Please send ad id in props.');
      return;
    }
    if (!placementId) {
      alert('Please send placementId in props.');
      return;
    }

    window.MediaAlphaExchange = {
      data: {
        zip: zipcode,
      },
      error: () => {
        setLoading(false);
        alert('something went wrong while getting insurance details!!!');
      },
      placement_id: placementId,
      sub_1: window.location.href,
      sub_2: pagePlacement,
      success: () => {
        setLoading(false);
      },
      type,
      ua_class: uaClass,
      version,
    };
    setLoading(true);
    window.MediaAlphaExchange__load(adId);
    setPrimaryForm(false);
  }, [adId, pagePlacement, placementId, type, uaClass, version, zipcode]);

  const onSubmit = React.useCallback(() => {
    submitForm();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [zipcode, submitForm]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      submitForm();
    }
  };

  const handleZipCodeChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setZipcode(event.target.value);
  }, []);

  return (
    <GetQuotesContainer>
      <div className="bz3-insurance-form media-alpha max-w-3xl m-auto ">
        {primaryform && (
          <>
            <h5>{title}</h5>
            <h6>{subtitle}</h6>
            <div>
              <div className="inline-media-alpha-insurance">
                <div className="input-group flex">
                  <input
                    className="input-group-field zip-code w-full"
                    name="zip_code"
                    onChange={handleZipCodeChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter Zip Code"
                    required={true}
                    type="text"
                    value={zipcode}
                  />
                  {/* <input
                    defaultValue={window.location.href}
                    name="href"
                    style={{ display: 'none' }}
                    type="text"
                  /> */}
                  <input defaultValue="top" name="placement" style={{ display: 'none' }} type="text" />
                  <div className="input-group-button">
                    <button className="button cursor-pointer" onClick={onSubmit} type="button">
                      {button_text?.length ? button_text : 'Get Quotes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {loading ? <div>Loading quotes</div> : ''}
        <div className="media-alpha-quote-container" id={adId} />
      </div>
    </GetQuotesContainer>
  );
};
const GetQuotesContainer = styled.div`
  label {
    display: block;
    margin: 0;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.8;
    color: #0a0a0a;
  }
  .bz3-insurance-form {
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 0 6px 1px hsl(0deg 0% 64% / 24%);
    transition: all 0.3s ease;
    color: #4c5159;
    border: 1px solid #d6d6d6;
    padding: 1rem;
    margin: 0 auto 1rem;
    &:hover {
      box-shadow: 0 0 7px 2px hsl(0deg 0% 64% / 50%);
    }
    h5 {
      margin-bottom: 0.5rem;
      font-size: 1.6875rem;
      line-height: 1.4;
      margin-top: 0;
      font-weight: 700;
      text-rendering: optimizeLegibility;
      @media screen and (max-width: 640px) {
        margin-bottom: 5px;
        font-size: 20px;
      }
    }

    h6 {
      font-style: italic;
      color: #8a8a8a;
      font-size: 1rem;
      line-height: 1.4;
      margin-top: 0;
      font-weight: 700;
      margin-bottom: 0.5rem;
      text-rendering: optimizeLegibility;
      @media screen and (max-width: 640px) {
        font-size: 15px;
      }
    }
    .input-group {
      border-radius: 4px;
      overflow: hidden;
      border: 1px solid #ccc;
      .input-group-field {
        margin-bottom: 0;
        border: none;
        border-right: 1px solid #ccc;
        height: 44px;
        border-radius: 0;
        box-shadow: none;
        margin: 0;
        white-space: nowrap;
        @media screen and (max-width: 640px) {
          margin-bottom: 0;
          border: none;
          border: 1px solid #ccc;
          font-size: 14px;
          height: 33px;
        }
      }
      .button {
        border-radius: 0 4px 4px 0;
        height: 44px;
        @media screen and (max-width: 640px) {
          border-radius: 0 4px 4px 0;
          height: 33px;
          line-height: 33px;
          font-size: 14px;
        }
      }
    }
    .button,
    #__max_submit {
      white-space: nowrap;
      display: inline-block;
      height: 40px;
      line-height: 40px;
      padding: 0 14px;
      font-weight: 600;
      text-decoration: none;
      -webkit-transform: translateY(0);
      -o-transform: translateY(0);
      transform: translateY(0);
      -webkit-transition: all 0.15s ease-in-out !important;
      -o-transition: all 0.15s ease-in-out !important;
      transition: all 0.15s ease-in-out !important;
      -webkit-box-shadow: 0 1px 3px 0 rgb(0 0 0 / 21%);
      box-shadow: 0 1px 3px 0 rgb(0 0 0 / 21%);
      border: 0;
      border-radius: 4px;
      color: #fefefe !important;
      font-family: sans-serif;
      font-size: 16px;
      letter-spacing: 0.09em;
      background-color: #ff7b00;
      text-shadow: 1px 0 0 rgb(0 0 0 / 10%);
    }
  }
  .media-alpha-quote-container {
    > div {
      box-shadow: none !important;
      margin: 0 !important;
    }
  }
`;
